import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { components } from '@/lib/api/generated'
import { apiClient } from '@/lib/api/client'

export type AuthUser = components['schemas']['UserResponse']

type AuthState = {
  user: AuthUser | null
  token: string | null
  status: 'idle' | 'hydrating' | 'ready'
  login: (creds: { username: string; password: string }) => Promise<{ ok: true } | { ok: false; status: number; message: string }>
  logout: (opts?: { skipServerCall?: boolean }) => Promise<void>
  hydrate: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      status: 'idle',

      login: async ({ username, password }) => {
        const { data, error, response } = await apiClient.POST('/api/auth/login', {
          body: { username, password },
        })
        if (error || !data) {
          const detail = (error as { detail?: string } | undefined)?.detail
          const message =
            response.status === 401
              ? 'Invalid username or password'
              : detail || `Login failed (${response.status})`
          return { ok: false, status: response.status, message }
        }
        set({ token: data.access_token, user: data.user, status: 'ready' })
        return { ok: true }
      },

      logout: async ({ skipServerCall = false } = {}) => {
        const token = get().token
        if (!skipServerCall && token) {
          // Fire-and-forget — server logs the action; we don't block the redirect on it.
          apiClient.POST('/api/auth/logout', {}).catch(() => {})
        }
        set({ token: null, user: null, status: 'ready' })
      },

      hydrate: async () => {
        const token = get().token
        if (!token) {
          set({ status: 'ready' })
          return
        }
        set({ status: 'hydrating' })
        const { data, error } = await apiClient.GET('/api/auth/me', {})
        if (error || !data) {
          set({ token: null, user: null, status: 'ready' })
          return
        }
        set({ user: data, status: 'ready' })
      },
    }),
    {
      name: 'dh-auth',
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
)
